import { Outlet, Link, useLocation } from 'react-router-dom'
import { Layout as LayoutIcon, ChefHat, BookOpen, Layers, Flag, ScanSearch, LogOut } from 'lucide-react'
import { UserButton } from '@clerk/clerk-react'
import { useAppAuth } from '@/auth/AuthProvider'

const ROLE_LABEL = { admin: 'Admin', reviewer: 'Reviewer', approver: 'Approver' }

export function Layout() {
  const location = useLocation()
  const { user, clerkEnabled, signOut } = useAppAuth()
  const role = user?.role || 'reviewer'

  const navItems = [
    { path: '/', label: 'Dashboard', icon: LayoutIcon, roles: ['admin', 'reviewer', 'approver'], testId: 'nav-dashboard' },
    { path: '/review', label: 'Review', icon: ScanSearch, roles: ['reviewer', 'approver'], testId: 'nav-review' },
    { path: '/flagged', label: 'Flagged', icon: Flag, roles: ['approver'], testId: 'nav-flagged' },
    { path: '/batch', label: 'Batch Jobs', icon: Layers, roles: ['admin'], testId: 'nav-batch' },
    { path: '/prompts', label: 'Prompt Library', icon: BookOpen, roles: ['admin'], testId: 'nav-prompts' },
  ].filter((i) => i.roles.includes(role))

  const isActive = (path) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path)

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-8">
              <Link to="/" className="flex items-center gap-2 text-xl font-bold text-gray-900">
                <ChefHat className="h-6 w-6 text-orange-500" />
                <span>Food Image Transformer</span>
              </Link>
              <nav className="hidden md:flex items-center gap-1">
                {navItems.map((item) => (
                  <Link
                    key={item.path}
                    data-testid={`nav-${item.path === '/' ? 'dashboard' : item.path.slice(1)}`}
                    to={item.path}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isActive(item.path)
                        ? 'bg-orange-50 text-orange-600'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                  >
                    <item.icon className="h-4 w-4" />
                    {item.label}
                  </Link>
                ))}
              </nav>
            </div>
            <div className="flex items-center gap-3">
              <span data-testid="role-badge" className="hidden sm:inline text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600">
                {ROLE_LABEL[role] || role}
              </span>
              {clerkEnabled ? (
                <UserButton afterSignOutUrl="/" />
              ) : (
                <button
                  onClick={signOut}
                  className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700"
                  title="Auth disabled (dev)"
                >
                  <LogOut className="h-4 w-4" />
                  {user?.name || 'Dev'}
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Outlet />
      </main>
    </div>
  )
}
